/**
 * Project store for the code surface — persisted in IndexedDB (via the same
 * idb instance the rest of the app uses) so the editor + preview share state
 * across navigation and reloads. Multimodal context: agents and studio can
 * read this same store via the helper getters below.
 */
import { create } from 'zustand';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import type { ProjectFile, Task, SystemLog } from './types';

interface CodeProjectState {
  files: ProjectFile[];
  /** Tasks the agent / annotator have opened against the running preview. */
  tasks: Task[];
  /** Bundler + runtime logs. */
  logs: SystemLog[];
  /** Currently selected file path. */
  activePath: string | null;

  setFiles(f: ProjectFile[]): void;
  upsertFile(f: ProjectFile): void;
  deleteFile(name: string): void;
  setActivePath(p: string | null): void;
  pushLog(l: SystemLog): void;
  clearLogs(): void;
  addTask(t: Task): void;
  updateTask(id: string, patch: Partial<Task>): void;
  exportZip(): Promise<void>;
}

export const useCodeProject = create<CodeProjectState>((set, get) => ({
  files: [],
  tasks: [],
  logs: [],
  activePath: null,

  setFiles: (f) => set({ files: f, activePath: get().activePath ?? f[0]?.name ?? null }),
  upsertFile: (f) => set((s) => {
    const idx = s.files.findIndex((x) => x.name === f.name);
    if (idx >= 0) {
      const copy = s.files.slice();
      copy[idx] = f;
      return { files: copy };
    }
    return { files: [...s.files, f] };
  }),
  deleteFile: (name) => set((s) => ({ files: s.files.filter((x) => x.name !== name) })),
  setActivePath: (p) => set({ activePath: p }),
  pushLog: (l) => set((s) => ({ logs: [...s.logs.slice(-499), l] })),
  clearLogs: () => set({ logs: [] }),
  addTask: (t) => set((s) => ({ tasks: [...s.tasks, t] })),
  updateTask: (id, patch) => set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),

  exportZip: async () => {
    const zip = new JSZip();
    for (const f of get().files) {
      zip.file(f.name, f.content);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `protolive-code-${Date.now()}.zip`);
  },
}));
